/**
 * Remote AI calls. Fall back to the local heuristic engine (lib/ai.ts) when
 * VITE_AI_ENABLED isn't set or the network request fails — so AI always works
 * in preview mode.
 */
import { useAuthStore } from '@/stores/authStore';
import type { GeneratedBoard, AskAnswer } from '@/lib/ai';
import {
  generateBoard as localGenerate,
  summarizeBoard as localSummarize,
  askData as localAsk,
} from '@/lib/ai';

export const isAiBackendLive = (): boolean => {
  const flag = (import.meta.env.VITE_AI_ENABLED as string | undefined) ?? '';
  return flag === '1' || flag.toLowerCase() === 'true';
};

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function post<T>(action: string, body: unknown): Promise<T> {
  const res = await fetch(`/api/ai?action=${action}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `ai ${action} failed`);
  }
  return res.json() as Promise<T>;
}

export async function remoteGenerateBoard(prompt: string): Promise<GeneratedBoard> {
  if (!isAiBackendLive()) return localGenerate(prompt);
  try {
    return await post<GeneratedBoard>('generate', { prompt });
  } catch (err) {
    console.warn('[ai] remote generate failed, falling back:', err);
    return localGenerate(prompt);
  }
}

export async function remoteSummarizeBoard(boardId: string): Promise<string> {
  if (!isAiBackendLive()) return localSummarize(boardId);
  try {
    const { text } = await post<{ text: string }>('summarize', { boardId });
    return text;
  } catch (err) {
    console.warn('[ai] remote summarize failed, falling back:', err);
    return localSummarize(boardId);
  }
}

export async function remoteAskData(question: string): Promise<AskAnswer> {
  if (!isAiBackendLive()) return localAsk(question);
  try {
    return await post<AskAnswer>('ask', { question });
  } catch (err) {
    console.warn('[ai] remote ask failed, falling back:', err);
    return localAsk(question);
  }
}
